import React, { useState, useEffect, useRef } from "react";
import { Container, Row, Col, Form, Button, Modal } from "react-bootstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { add_kyc, get_kyc_list, kyc_update, kyc_delete } from "../../../api/ControllApi";

// ─── Colour tokens ────────────────────────────────────────────────────────────
const GREEN = "#709342";

const EMPTY_FORM = {
  aadhar_name: "",
  aadhar_number: "",
  pan_number: "",
  aadhar_doc: null,
  pan_doc: null,
  signature_doc: null,
};

const PREVIEW_INIT = { aadhar: null, pan: null, signature: null };

// ══════════════════════════════════════════════════════════════════════════════
export default function KYCForm() {
  const [kycList, setKycList] = useState([]);
  const [view, setView] = useState("list");
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [previews, setPreviews] = useState(PREVIEW_INIT);
  const [loading, setLoading] = useState(false);

  const [updateItem, setUpdateItem] = useState(null);


  // ── Delete modal ────────────────────────────────────────────────────────────
  const [showDelete, setShowDelete] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // ── helpers ─────────────────────────────────────────────────────────────────
  const resetForm = () => { setForm(EMPTY_FORM); setErrors({}); setPreviews(PREVIEW_INIT); setUpdateItem(null); };
  const openAdd = () => { resetForm(); setView("form"); };

  const fetchKycList = async () => {
    try {
      const res = await get_kyc_list();
      if (res?.data) {
        setKycList(
          res.data.map((item) => ({
            id: item.id,
            isSelf: item.is_self ?? false,
            aadhar_name: item.name,
            aadhar_number: item.aadhar_no,
            pan_number: item.pan_no,
            aadhar_doc: item.aadhar_doc,
            pan_doc: item.pan_doc,
            signature_doc: item.signature_doc || item.sign_doc, // Robust mapping
          }))
        );
      }
    } catch (err) {

    }
  };

  useEffect(() => { fetchKycList(); }, []);

  // ── update handlers ─────────────────────────────────────────────────────────
  const openUpdate = (item) => {
    setUpdateItem(item);
    setForm({
      aadhar_name: item.aadhar_name || "",
      aadhar_number: item.aadhar_number || "",
      pan_number: item.pan_number || "",
      aadhar_doc: null,
      pan_doc: null,
      signature_doc: null,
    });
    setPreviews({
      aadhar: item.aadhar_doc || null,
      pan: item.pan_doc || null,
      signature: item.signature_doc || null,
    });
    setErrors({});
    setView("form");
  };

  // ── delete handlers ─────────────────────────────────────────────────────────
  const openDelete = (item) => { setDeleteItem(item); setShowDelete(true); };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await kyc_delete(deleteItem.id);
      toast.success("KYC Deleted Successfully!");
      setShowDelete(false);
      await fetchKycList();
    } catch (err) {

      toast.error(err?.response?.data?.message || "Failed to delete KYC.", { autoClose: 3000 });
    } finally {
      setDeleteLoading(false);
    }
  };

  // ── validation ───────────────────────────────────────────────────────────────
  const validate = () => {
    const e = {};
    const isAdd = !updateItem;

    // ── Text fields ──────────────────────────────────────────────────────────
    if (isAdd && !form.aadhar_name.trim()) {
      e.aadhar_name = "Name is required";
    }

    if (!form.aadhar_number.trim()) {
      if (isAdd) e.aadhar_number = "Aadhar Number is required";
    } else if (!/^\d{12}$/.test(form.aadhar_number.trim())) {
      e.aadhar_number = "Aadhar Number must be exactly 12 digits";
    }

    if (!form.pan_number.trim()) {
      if (isAdd) e.pan_number = "PAN Number is required";
    } else if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(form.pan_number.trim())) {
      e.pan_number = "Invalid PAN format — should be like ABCDE1234F";
    }

    // ── Documents — required only in Add mode; signature always optional ──
    if (isAdd) {
      if (!form.aadhar_doc) e.aadhar_doc = "Aadhar document is required";
      if (!form.pan_doc)    e.pan_doc    = "PAN document is required";
    }

    setErrors(e);
    return e;
  };


  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (files && files[0]) {
      if (files[0].size / (1024 * 1024) > 2) {
        setErrors((prev) => ({ ...prev, [name]: "File too large. Max 2 MB." }));
        e.target.value = "";
        setForm((prev) => ({ ...prev, [name]: null }));
        return;
      }
      const previewUrl = URL.createObjectURL(files[0]);
      const key = name.split("_")[0]; // aadhar, pan, signature
      setPreviews((prev) => ({ ...prev, [key]: previewUrl }));
    }
    setForm((prev) => ({ ...prev, [name]: files ? files[0] : value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const ve = validate();
    if (Object.keys(ve).length > 0) {
      toast.error(Object.values(ve)[0], { autoClose: 3000 });
      return;
    }
    setLoading(true);
    try {
      if (updateItem) {
        // ── UPDATE MODE (JSON) ────────────────────────────────────────────────
        const payload = {
          name: form.aadhar_name.trim(),
          aadhar_no: form.aadhar_number.trim(),
          pan_no: form.pan_number.trim(),
        };
        await kyc_update(updateItem.id, payload);
        toast.success("KYC Updated Successfully!");
      } else {
        // ── ADD MODE (FormData) ───────────────────────────────────────────────
        const fd = new FormData();
        fd.append("name[]", form.aadhar_name);
        fd.append("pan_no[]", form.pan_number);
        fd.append("aadhar_no[]", form.aadhar_number);
        if (form.aadhar_doc instanceof File) fd.append("aadhar_doc[]", form.aadhar_doc);
        if (form.pan_doc instanceof File) fd.append("pan_doc[]", form.pan_doc);
        if (form.signature_doc instanceof File) fd.append("sign_doc[]", form.signature_doc);
        await add_kyc(fd);
        toast.success("KYC Saved Successfully!");
      }
      
      await fetchKycList();
      resetForm();
      setView("list");
    } catch (err) {
      if (err?.response?.data?.errors) {
        const apiErr = err.response.data.errors;
        const ne = {};
        Object.keys(apiErr).forEach((k) => {
          if (k.includes("aadhar_doc")) ne.aadhar_doc = "Aadhar file error. Max 2MB.";
          if (k.includes("pan_doc")) ne.pan_doc = "PAN document error. Max 2MB.";
          if (k.includes("signature_doc")) ne.signature_doc = "Signature file error. Max 2MB.";
          if (k.includes("aadhar_no")) ne.aadhar_number = "Invalid Aadhar number.";
          if (k.includes("pan_no")) ne.pan_number = "Invalid PAN number.";
        });
        setErrors(ne);
        toast.error(Object.values(ne)[0] || "Something went wrong.", { autoClose: 3000 });
      } else {
        toast.error(err?.response?.data?.message || "Oops! Something went wrong.", { autoClose: 3000 });
      }
    } finally {
      setLoading(false);
    }
  };

  // ── render ───────────────────────────────────────────────────────────────────
  return (
    <Container className="py-3 py-md-4" style={{ maxWidth: 1000 }}>
      <ToastContainer position="top-center" />

      {/* ════════════ DELETE MODAL ════════════ */}
      <Modal show={showDelete} onHide={() => setShowDelete(false)} centered>
        <Modal.Header style={ms.headerDanger} closeButton closeVariant="white">
          <Modal.Title style={{ fontSize: "1rem", fontWeight: 700 }}>Delete KYC</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <p className="mb-0" style={{ fontSize: 14, color: "#444" }}>
            Are you sure you want to delete KYC for{" "}
            <strong>{deleteItem?.aadhar_name}</strong>? This cannot be undone.
          </p>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0">
          <Button variant="secondary" onClick={() => setShowDelete(false)} style={{ borderRadius: 8 }}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete} disabled={deleteLoading} style={{ borderRadius: 8 }}>
            {deleteLoading ? "Deleting…" : "Delete"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ════════════ LIST VIEW ════════════ */}
      {view === "list" && (
        <div style={s.card}>
          <div style={s.header}>
            <span className="d-none d-sm-block" style={{ width: 110, flexShrink: 0 }} />
            <span style={{ flex: 1, textAlign: "center" }}>KYC Details</span>
            <button style={s.addBtn} onClick={openAdd}>
              <span style={{ fontSize: 18, lineHeight: 1 }}>＋</span>
              <span className="d-none d-sm-inline">Add KYC</span>
            </button>
          </div>

          <div className="p-3 p-md-4">
            {kycList.length === 0 ? (
              <div style={s.empty}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>📋</div>
                <p style={{ color: "#888", marginBottom: 0 }}>
                  No KYC records found. Click <strong>Add KYC</strong> to get started.
                </p>
              </div>
            ) : (
              <Row className="gy-3">
                {kycList.map((item) => (
                  <Col xs={12} md={6} key={item.id}>
                    <KycCard
                      item={item}
                      onUpdate={() => openUpdate(item)}
                      onDelete={() => openDelete(item)}
                    />
                  </Col>
                ))}
              </Row>
            )}
          </div>
        </div>
      )}

      {/* ════════════ FORM VIEW ════════════ */}
      {view === "form" && (
        <div style={s.card}>
          <div style={{ ...s.header, justifyContent: "center" }}>
            {updateItem ? "Update KYC" : "Add KYC"}
          </div>
          <div className="p-3 p-md-5">
            <Form onSubmit={handleSubmit} noValidate>
              <Row className="gy-3 gy-md-4 gx-3 gx-md-5">

                <Col xs={12} md={6}>
                  <Form.Label style={s.label}>
                    Name as per Aadhar Card *
                  </Form.Label>
                  <Form.Control
                    style={{ ...s.input, borderColor: errors.aadhar_name ? "#dc3545" : "#ced4da" }}
                    name="aadhar_name"
                    placeholder="Enter name as on Aadhar"
                    value={form.aadhar_name}
                    onChange={handleChange}
                  />
                  {errors.aadhar_name && <small style={s.errorText}>{errors.aadhar_name}</small>}
                </Col>

                <Col xs={12} md={6}>
                  <Form.Label style={s.label}>
                    Aadhar Number *
                  </Form.Label>
                  <Form.Control
                    style={{ ...s.input, borderColor: errors.aadhar_number ? "#dc3545" : "#ced4da" }}
                    name="aadhar_number"
                    placeholder="Enter 12-digit Aadhar Number"
                    value={form.aadhar_number}
                    onChange={(e) => { e.target.value = e.target.value.replace(/\D/g, ""); handleChange(e); }}
                    maxLength="12"
                    inputMode="numeric"
                  />
                  {errors.aadhar_number && <small style={s.errorText}>{errors.aadhar_number}</small>}
                </Col>

                <Col xs={12} md={6}>
                  <Form.Label style={s.label}>
                    PAN Number *
                  </Form.Label>
                  <Form.Control
                    style={{ ...s.input, borderColor: errors.pan_number ? "#dc3545" : "#ced4da" }}
                    name="pan_number"
                    placeholder="e.g. ABCDE1234F"
                    value={form.pan_number}
                    onChange={(e) => { e.target.value = e.target.value.toUpperCase(); handleChange(e); }}
                    maxLength="10"
                  />
                  {errors.pan_number && <small style={s.errorText}>{errors.pan_number}</small>}
                </Col>

                <Col xs={12} md={6}>
                  <Form.Label style={s.label}>
                    Upload Aadhar Document *{" "}
                    <span className="text-muted fw-normal">(Max 2 MB)</span>
                  </Form.Label>
                  <Form.Control
                    type="file" name="aadhar_doc" accept=".png,.jpg,.jpeg,.pdf"
                    style={{ ...s.fileInput, borderColor: errors.aadhar_doc ? "#dc3545" : "#ced4da" }}
                    onChange={handleChange}
                  />
                  <FilePreview url={previews.aadhar} />
                  {errors.aadhar_doc && <small style={s.errorText}>{errors.aadhar_doc}</small>}
                </Col>

                <Col xs={12} md={6}>
                  <Form.Label style={s.label}>
                    Upload PAN Document *{" "}
                    <span className="text-muted fw-normal">(Max 2 MB)</span>
                  </Form.Label>
                  <Form.Control
                    type="file" name="pan_doc" accept=".png,.jpg,.jpeg,.pdf"
                    style={{ ...s.fileInput, borderColor: errors.pan_doc ? "#dc3545" : "#ced4da" }}
                    onChange={handleChange}
                  />
                  <FilePreview url={previews.pan} />
                  {errors.pan_doc && <small style={s.errorText}>{errors.pan_doc}</small>}
                </Col>

                <Col xs={12} md={6}>
                  <Form.Label style={s.label}>
                    Upload Signature{" "}
                    <span className="text-muted fw-normal">(Optional — Max 2 MB)</span>
                  </Form.Label>
                  <Form.Control
                    type="file" name="signature_doc" accept=".png,.jpg,.jpeg,.pdf"
                    style={{ ...s.fileInput, borderColor: errors.signature_doc ? "#dc3545" : "#ced4da" }}
                    onChange={handleChange}
                  />
                  <FilePreview url={previews.signature} />
                  {errors.signature_doc && <small style={s.errorText}>{errors.signature_doc}</small>}
                </Col>

              </Row>

              <div className="d-flex justify-content-center gap-3 mt-4 mt-md-5">
                <Button
                  variant="outline-secondary"
                  style={{ borderRadius: 8, padding: "9px 28px" }}
                  onClick={() => setView("list")}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={loading} style={s.solidBtn}>
                  {loading ? "Saving…" : (updateItem ? "Update" : "Save")}
                </Button>
              </div>
            </Form>
          </div>
        </div>
      )}
    </Container>
  );
}

// ─── FilePreview ──────────────────────────────────────────────────────────────
function FilePreview({ url }) {
  if (!url) return null;
  // Robust image detection: extension check or blob check or "signature" in URL
  const isImage = url.match(/\.(jpeg|jpg|gif|png|webp|avif)/i) || 
                 (url.startsWith('blob:') && !url.includes('pdf')) ||
                 url.toLowerCase().includes('signature') || 
                 url.toLowerCase().includes('sign_doc');
  
  return (
    <div className="mt-2 d-flex align-items-center gap-2">
      {isImage ? (
        <a href={url} target="_blank" rel="noopener noreferrer">
          <img 
            src={url} 
            alt="Preview" 
            style={{ width: 60, height: 60, objectFit: "cover", borderRadius: 8, border: "2px solid #eee", cursor: "pointer" }} 
            title="Click to view full image"
          />
        </a>
      ) : (
        <a 
          href={url} 
          target="_blank" 
          rel="noopener noreferrer"
          style={{ fontSize: 13, color: GREEN, fontWeight: 700, textDecoration: "none", border: "1px solid " + GREEN, padding: "4px 10px", borderRadius: 6 }}
        >
          📄 View/Download PDF
        </a>
      )}
      {isImage && (
        <a 
          href={url} 
          download 
          target="_blank"
          style={{ fontSize: 12, color: GREEN, fontWeight: 700, textDecoration: "none" }}
          title="Download Image"
        >
          ⬇️
        </a>
      )}
    </div>
  );
}

// ─── KycCard ──────────────────────────────────────────────────────────────────
function KycCard({ item, onUpdate, onDelete }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  return (
    <div style={kc.card}>
      {/* 3-dot menu */}
      <div ref={ref} style={kc.menuWrap}>
        <button style={kc.dotsBtn} onClick={() => setMenuOpen((v) => !v)} title="Options">⋮</button>
        {menuOpen && (
          <div style={kc.dropdown}>
            <button style={kc.dropItem} onClick={() => { setMenuOpen(false); onUpdate(); }}>
              ✏️&nbsp; Update
            </button>
            <div style={{ height: 1, background: "#f0f0f0" }} />
            <button style={{ ...kc.dropItem, color: "#dc3545" }} onClick={() => { setMenuOpen(false); onDelete(); }}>
              🗑️&nbsp; Delete
            </button>
          </div>
        )}
      </div>

      <div style={kc.detailLabel} className="mb-1">Name</div>
      <div style={kc.name}>{item.aadhar_name}</div>

      <div className="d-flex justify-content-between mb-2">
        <span style={kc.detailLabel}>Aadhar</span>
        <span style={kc.detailVal}>•••• •••• {item.aadhar_number?.slice(-4) || "0000"}</span>
      </div>
      <div className="d-flex justify-content-between mb-2">
        <span style={kc.detailLabel}>PAN</span>
        <span style={kc.detailVal}>{item.pan_number}</span>
      </div>

      <div className="d-flex flex-wrap gap-2 mt-3">
        <span style={kc.docChip}>{item.aadhar_doc ? "✅ Aadhar" : "⚠️ Aadhar"}</span>
        <span style={kc.docChip}>{item.pan_doc ? "✅ PAN" : "⚠️ PAN"}</span>
        {item.signature_doc && <span style={kc.docChip}>✅ Signature</span>}
      </div>
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const s = {
  card: { border: "1px solid #e0e0e0", borderRadius: 12, overflow: "hidden", backgroundColor: "#fff", boxShadow: "0 4px 16px rgba(0,0,0,0.07)" },
  header: { backgroundColor: GREEN, color: "#fff", padding: "14px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: "1.05rem", fontWeight: 700 },
  addBtn: { background: "#fff", color: GREEN, border: "none", borderRadius: 8, padding: "6px 14px", fontWeight: 700, fontSize: 14, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 },
  empty: { textAlign: "center", padding: "50px 20px" },
  label: { fontSize: 14, fontWeight: 700, color: "#444", marginBottom: 6, display: "block" },
  input: { height: 45, borderRadius: 6, fontSize: 14 },
  fileInput: { height: 45, borderRadius: 6, fontSize: 14, padding: 8 },
  solidBtn: { backgroundColor: GREEN, border: "none", padding: "9px 32px", fontSize: 15, fontWeight: 700, borderRadius: 8, color: "#fff" },
  errorText: { color: "#dc3545", fontSize: 12, marginTop: 4, display: "block" },
};

const kc = {
  card: { borderRadius: 12, padding: 20, background: "#fafafa", border: "1px solid #eee", height: "100%", position: "relative" },
  name: { fontWeight: 800, fontSize: 16, color: "#222", marginBottom: 14 },
  detailLabel: { fontSize: 12, fontWeight: 700, color: "#999" },
  detailVal: { fontSize: 13, color: "#444", fontWeight: 600 },
  docChip: { fontSize: 11, background: "#f0f0f0", borderRadius: 20, padding: "4px 12px", color: "#555" },
  menuWrap: { position: "absolute", top: 12, right: 12, zIndex: 10 },
  dotsBtn: { background: "none", border: "none", fontSize: 22, cursor: "pointer", color: "#888", padding: "0 4px", lineHeight: 1, borderRadius: 6 },
  dropdown: { position: "absolute", right: 0, top: "110%", background: "#fff", border: "1px solid #e0e0e0", borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.12)", minWidth: 140, zIndex: 100, overflow: "hidden" },
  dropItem: { display: "block", width: "100%", padding: "10px 16px", background: "none", border: "none", textAlign: "left", fontSize: 13, fontWeight: 600, cursor: "pointer", color: "#333" },
};

const ms = {
  header: { backgroundColor: GREEN, color: "#fff", border: "none" },
  headerDanger: { backgroundColor: "#dc3545", color: "#fff", border: "none" },
};